import React, { useState, useEffect } from 'react';
import { Modal, Button, Switch, InputNumber, Slider } from 'antd';
import { CheckOutlined, DownOutlined, TagFilled } from '@ant-design/icons';
import './PricingPlans.scss';
import pricingData from '../../data/pricingData.json';
import { PORTAL_LINK } from '../../../../../../environments/env';
import { getTierPlanName } from '../../utils/tierUtils';
const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
];
const PricingPlans = ({
    visible,
    onClose,
    setAnnualSetting,
    selectedPlan,
    currentlyActiveSubscription,
    openPaymentConfirmationModal
}) => {
    const [annual, setAnnual] = useState(true);
    const [selectedMaxTier, setSelectedMaxTier] = useState(0);
    const [expandedCards, setExpandedCards] = useState({});
    const [selectedPlanType, setSelectedPlanType] = useState("subscriptions");
    const [bundleImageQty, setBundleImageQty] = useState(5);

    const { plans, enterprise, advancedFeaturesTable, discountPercentage, creditBundle } = pricingData;
    const regularPlans = plans.filter(p => p.id !== 'business' && p.id !== 'free_trial_ai');
    const businessPlan = plans.find(p => p.id === 'business');

    const toggleCard = (key) => {
        setExpandedCards(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    useEffect(() => {
        if (selectedPlan) {
            setSelectedPlanType(selectedPlan);
        }
    }, [selectedPlan]);

    const getAnnualMonthlyPrice = (monthlyPrice) => {
        return Math.round(monthlyPrice * (1 - discountPercentage / 100));
    };

    const getDisplayPrice = (plan) => {
        if (plan.price === 0 || plan.price === null) return plan.price;
        if (plan.id === 'business_ai' && plan.tiers) {
            const tier = plan.tiers[selectedMaxTier];
            return annual ? Math.round(tier.annualPriceTotal / 12) : tier.price;
        }
        return annual ? getAnnualMonthlyPrice(plan.price) : plan.price;
    };

    const getDisplayCredits = (plan) => {
        if (plan.id === 'business_ai' && plan.tiers) {
            return plan.tiers[selectedMaxTier].credits;
        }
        return plan.credits;
    };

    const getDisplayFeatures = (plan) => {
        if (plan.id === 'business_ai' && plan.tiers) {
            return plan.tiers[selectedMaxTier].features;
        }
        return plan.features;
    };

    const getAdvancedFeaturesForPlan = (planId) => {
        return advancedFeaturesTable.categories.flatMap(category =>
            Object.entries(category.features).map(([name, planValues]) => ({
                name,
                included: !!planValues[planId],
                value: typeof planValues[planId] === 'string' ? planValues[planId] : null
            }))
        );
    };

    const getAnnualTotal = (plan) => {
        if (plan.id === 'business_ai' && plan.tiers) {
            return plan.tiers[selectedMaxTier].annualPriceTotal;
        }
        return getAnnualMonthlyPrice(plan.price) * 12;
    };

    const getSubscriptionButtonText = (plan) => {
        const isCurrentPlan = plan.id === currentlyActiveSubscription || `annual_${plan.id}` === currentlyActiveSubscription;
        if (isCurrentPlan) {
            return 'Renew Subscription';
        }

        if (currentlyActiveSubscription) {
            return `Switch to ${plan.name}`;
        }

        return plan.buttonText;
    };

    return (
        <Modal
            centered
            closable
            maskClosable={false}
            visible={visible}
            onCancel={onClose}
            footer={null}
            className="pricing-plans-modal"
            destroyOnClose={true}
            width={700}
        >

            <div className="pricing-plans-content">
                <div>
                    <div className="manrope f-20 w-500 black-085 lh-28 mb-24">
                        Select a bundle or a subscription
                    </div>

                    <div className={`subscription-selector-wrapper justify-space-between mb-40`}>
                        <div className="subscription-selector">

                             <div
                                className={`plan ${selectedPlanType === "subscriptions" ? "selected" : ""
                                    }`}
                                onClick={() => setSelectedPlanType("subscriptions")}>
                                <span className="manrope f-14 w-700 lh-22">Subscribe & Save</span>
                            </div>
                            <div
                                className={`plan ${selectedPlanType === "bundles" ? "selected" : ""}`}
                                onClick={() => setSelectedPlanType("bundles")}>
                                <span className="manrope f-14 w-700 lh-22">Buy a bundle</span>
                            </div>

                        </div>

                        {selectedPlanType === "subscriptions" &&
                            <div className="switch-wrapper">
                                <span className={`manrope f-12 lh-20 black-085 ${annual ? 'w-500' : 'w-700'} mr-8`}>Monthly</span>
                                <Switch
                                    className='subscription-selector-switch'
                                    checked={annual}
                                    onChange={(checked) => {
                                        setAnnual(checked);
                                        setAnnualSetting(checked);
                                    }} />
                                <span className={`manrope f-12 lh-20 black-085 ${annual ? 'w-700' : 'w-500'} ml-8`}>Annually</span>
                                <div className={`ml-8 save-discount-bg ${annual ? 'blue' : 'gray'}`}>
                                    <span className={`manrope f-12 lh-20 ${annual ? 'w-700 blue-18' : 'w-500 black-085'}`}>Save {discountPercentage}%</span>
                                </div>
                            </div>}
                    </div>

                    {selectedPlanType === "subscriptions" &&
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                {regularPlans.map((plan, index) => (
                                    <div key={plan.id} style={{ gap: 24 }}>
                                        <div className="pricing-plan-card justify-space-between">
                                            <div>
                                                {plan.isPopular && (
                                                    <div className="card-subtitle manrope f-14 w-500 lh-20 black-085 mb-8">Most Popular</div>
                                                )}
                                                <div className="manrope f-20 w-700 lh-32 black-085">
                                                    {plan.name}
                                                </div>
                                                <div className="manrope f-14 w-500 lh-24 black-085 mb-8">
                                                    {getDisplayCredits(plan).toLocaleString()} credits per month
                                                </div>
                                                <div className="justify-space-between align-baseline" style={{ gap: 16 }}>
                                                    <div className="justify-in-start align-baseline">
                                                        <span className="manrope f-26 w-700 lh-40 black-085">
                                                            ${getDisplayPrice(plan).toLocaleString()}
                                                        </span>
                                                        <span className="manrope f-16 w-500 lh-40 black-085">
                                                            /month
                                                        </span>
                                                    </div>
                                                    {annual && (
                                                        <div className="discount-wrapper">
                                                            <TagFilled className="discount-icon" />
                                                            <span className="discount manrope f-12 w-500 lh-20">Save {discountPercentage}% annually</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {plan.id === 'business_ai' && plan.tiers && (
                                                    <div className="slider-container" style={{ paddingBottom: 5 }}>
                                                        <Slider
                                                            min={0}
                                                            max={plan.tiers.length - 1}
                                                            step={1}
                                                            dots
                                                            value={selectedMaxTier}
                                                            onChange={(value) => setSelectedMaxTier(value)}
                                                            tooltipVisible={false}
                                                            trackStyle={{ backgroundColor: '#276dd7' }}
                                                            handleStyle={{ borderColor: '#276dd7', backgroundColor: '#276dd7' }}
                                                            marks={plan.tiers.reduce((marks, tier, idx) => {
                                                                marks[idx] = `${tier.multiplier}x`;
                                                                return marks;
                                                            }, {})}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="pricing-plans-actions">
                                                <div className="manrope f-14 w-400 lh-22 black-085 mb-16 text-center" style={{ opacity: 0.6 }}>
                                                    {annual ? `Billed annually at $${getAnnualTotal(plan).toLocaleString()}` : 'Billed monthly'}
                                                </div>
                                                <Button
                                                    onClick={() => {
                                                        if (plan.id === 'business_ai' && plan.tiers) {
                                                            const multiplier = plan.tiers[selectedMaxTier].multiplier;
                                                            openPaymentConfirmationModal(getTierPlanName(multiplier), 'subscription');
                                                        } else {
                                                            openPaymentConfirmationModal(plan.id, 'subscription');
                                                        }
                                                    }}
                                                    className="schedule-button mb-8">
                                                    {getSubscriptionButtonText(plan)}
                                                </Button>
                                                <div className="manrope f-12 w-400 lh-20 black-085 text-center">
                                                    {annual
                                                        ? `All ${(getDisplayCredits(plan) * 12).toLocaleString()} credits available immediately`
                                                        : `${getDisplayCredits(plan).toLocaleString()} credits available monthly`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`mobile-features-section expanded-radius`}>
                                            <div
                                                className={`mobile-features-header ${expandedCards[`sub_${index}`] ? `expanded-border` : ''}`}
                                                onClick={() => toggleCard(`sub_${index}`)}>
                                                <span className="manrope f-14 w-700 lh-22 black-085">Includes:</span>
                                                <div className={`expand-arrow ${expandedCards[`sub_${index}`] ? 'expanded' : ''}`}>
                                                    <DownOutlined className="manrope f-14 w-700 lh-14 black-085" />
                                                </div>
                                            </div>

                                            {expandedCards[`sub_${index}`] &&
                                                <div className="mobile-features-content">
                                                    <div className="mobile-features-list">
                                                        {getDisplayFeatures(plan).map((feature, idx) => (
                                                            <div key={idx} className="feature-row">
                                                                <span className="feature-name manrope f-14 w-700 lh-22 black-085">{feature}</span>
                                                                <span className="feature-value justify-in-center">
                                                                    <div className="checkmark">
                                                                        <CheckOutlined className="manrope f-12 lh-12 w-700 white" />
                                                                    </div>
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="advanced-features-header">
                                                        <img src="/img/ai_creator/ai-twinkle.svg" alt="sparkle" />
                                                        <span className="manrope f-12 w-700 lh-22 white">Plus, these advanced editing tools and features</span>
                                                    </div>

                                                    <div className="mobile-features-list">
                                                        {getAdvancedFeaturesForPlan(plan.id).map((feature, idx) => (
                                                            feature.included && <div key={idx} className={`feature-row`}>
                                                                <span className="feature-name manrope f-14 w-700 lh-22 black-085">
                                                                    {feature.name}
                                                                </span>
                                                                <span className="feature-value justify-in-center">
                                                                    {feature.value ? (
                                                                        <span className="manrope f-14 w-500 lh-22 black-085 text-center">{feature.value}</span>
                                                                    ) : (
                                                                        <div className="checkmark">
                                                                            <CheckOutlined className="manrope f-12 lh-12 w-700 white" />
                                                                        </div>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {businessPlan && (
                                <div style={{ marginTop: 40 }}>
                                    <div className="manrope f-20 w-500 lh-28 black-085 justify-in-start mb-24">
                                        {enterprise.title}
                                    </div>
                                    <div className="pricing-plan-card justify-space-between" style={{ gap: 24 }}>
                                        <div>
                                            <div className="manrope f-20 w-700 lh-32 black-085">
                                                {businessPlan.name}
                                            </div>
                                            <div className="manrope f-16 w-500 lh-24 black-085 mb-8">
                                                {businessPlan.features.join(' \u2022 ')}
                                            </div>
                                        </div>

                                        <div className="pricing-plans-actions">
                                            <Button
                                                onClick={() => window.location.href = `${PORTAL_LINK}contact-us`}
                                                className="schedule-button mb-8">
                                                {businessPlan.buttonText}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className={`mobile-features-section expanded-radius`}>
                                        <div
                                            className={`mobile-features-header ${expandedCards['business'] ? `expanded-border` : ''}`}
                                            onClick={() => toggleCard('business')}>
                                            <span className="manrope f-14 w-700 lh-22 black-085">Includes:</span>
                                            <div className={`expand-arrow ${expandedCards['business'] ? 'expanded' : ''}`}>
                                                <DownOutlined className="manrope f-14 w-700 lh-14 black-085" />
                                            </div>
                                        </div>

                                        {expandedCards['business'] &&
                                            <div className="mobile-features-content">
                                                <div className="mobile-features-list">
                                                    {enterprise.features.map((feature, idx) => (
                                                        <div key={idx} className="feature-row">
                                                            <span className="feature-name manrope f-14 w-700 lh-22 black-085">{feature}</span>
                                                            <span className="feature-value justify-in-center">
                                                                <div className="checkmark">
                                                                    <CheckOutlined className="manrope f-12 lh-12 w-700 white" />
                                                                </div>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="advanced-features-header">
                                                    <img src="/img/ai_creator/ai-twinkle.svg" alt="sparkle" />
                                                    <span className="manrope f-12 w-700 lh-22 white">Plus, these advanced editing tools and features</span>
                                                </div>

                                                <div className="mobile-features-list">
                                                    {getAdvancedFeaturesForPlan('enterprise').map((feature, idx) => (
                                                        feature.included && <div key={idx} className={`feature-row`}>
                                                            <span className="feature-name manrope f-14 w-700 lh-22 black-085">
                                                                {feature.name}
                                                            </span>
                                                            <span className="feature-value justify-in-center">
                                                                {feature.value ? (
                                                                    <span className="manrope f-14 w-500 lh-22 black-085 text-center">{feature.value}</span>
                                                                ) : (
                                                                    <div className="checkmark">
                                                                        <CheckOutlined className="manrope f-12 lh-12 w-700 white" />
                                                                    </div>
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>}
                                    </div>
                                </div>
                            )}
                        </>}



                    {selectedPlanType === "bundles" && <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            <div>
                                <div className="pricing-plan-card justify-space-between">
                                    <div>
                                        <div className="card-subtitle manrope f-14 w-500 lh-20 black-085 mb-8">{creditBundle.subtitle}</div>
                                        <div className="manrope f-20 w-700 lh-32 black-085">
                                            {creditBundle.name}
                                        </div>
                                        <div className="manrope f-16 w-500 lh-24 black-085 mb-8">
                                            {creditBundle.description}
                                        </div>
                                        <div className="justify-space-between align-baseline" style={{ gap: 16 }}>
                                            <div className="justify-in-start align-baseline">
                                                <span className="manrope f-26 w-700 lh-40 black-085">
                                                    ${creditBundle.pricePerImage.toFixed(2)}
                                                </span>
                                                <span className="manrope f-16 w-500 lh-40 black-085">
                                                    /image
                                                </span>
                                            </div>
                                        </div>
                                        <div className="manrope f-14 w-500 lh-22 black-085" style={{ opacity: 0.6 }}>
                                            {creditBundle.creditsPerImage} credits per image &bull; Minimum {creditBundle.minImages} images ({creditBundle.minCredits} credits)
                                        </div>
                                    </div>

                                    <div className="pricing-plans-actions">
                                        <div className="justify-space-between mb-16">
                                            <div className="justify-in-end">
                                                <div className="manrope f-10 lh-22 black-085 w-500 mr-8">IMAGES</div>
                                                <InputNumber
                                                    min={creditBundle.minImages}
                                                    value={bundleImageQty}
                                                    onKeyDown={(e) => {
                                                        if ((["-", ".", " "].includes(e.key)) || (/[a-zA-Z\s]/.test(e.key) && !allowedKeys.includes(e.key))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(value) => {
                                                        if (value !== null && value >= creditBundle.minImages) {
                                                            setBundleImageQty(value);
                                                        }
                                                    }}
                                                    className="quantity-input manrope f-14 lh-22 black-085 w-400" />
                                            </div>
                                            <div className="manrope f-14 w-400 lh-22 black-085 text-center" style={{ opacity: 0.6 }}>
                                                {bundleImageQty} images &bull; {bundleImageQty * creditBundle.creditsPerImage} credits
                                            </div>
                                        </div>
                                        <div className="manrope f-14 w-400 lh-22 black-085 mb-16 text-center" style={{ opacity: 0.6 }}>
                                            Total: ${(bundleImageQty * creditBundle.pricePerImage).toFixed(2)}
                                        </div>
                                        <Button
                                            onClick={() => openPaymentConfirmationModal(creditBundle.id, 'bundle', bundleImageQty * creditBundle.creditsPerImage)}
                                            className="schedule-button mb-8">
                                            {creditBundle.buttonText}&nbsp;
                                            {bundleImageQty * creditBundle.creditsPerImage} credits
                                        </Button>
                                        <div className="manrope f-12 w-400 lh-20 black-085 text-center">
                                            {creditBundle.creditsPerImage} credits per image. All {bundleImageQty * creditBundle.creditsPerImage} credits available at once.
                                        </div>
                                    </div>
                                </div>
                                <div className={`mobile-features-section expanded-radius`}>
                                    <div
                                        className={`mobile-features-header ${expandedCards['credit_bundle_ai'] ? `expanded-border` : ''}`}
                                        onClick={() => toggleCard('credit_bundle_ai')}>
                                        <span className="manrope f-14 w-700 lh-22 black-085">Includes:</span>
                                        <div className={`expand-arrow ${expandedCards['credit_bundle_ai'] ? 'expanded' : ''}`}>
                                            <DownOutlined className="manrope f-14 w-700 lh-14 black-085" />
                                        </div>
                                    </div>

                                    {expandedCards['credit_bundle_ai'] &&
                                        <div className="mobile-features-content">
                                            <div className="mobile-features-list">
                                                {creditBundle.features.map((feature, idx) => (
                                                    <div key={idx} className="feature-row">
                                                        <span className="feature-name manrope f-14 w-700 lh-22 black-085">{feature}</span>
                                                        <span className="feature-value justify-in-center">
                                                            <div className="checkmark">
                                                                <CheckOutlined className="manrope f-12 lh-12 w-700 white" />
                                                            </div>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>}
                                </div>
                            </div>
                        </div>

                        {businessPlan && (
                            <div style={{ marginTop: 40 }}>
                                <div className="manrope f-20 w-500 lh-28 black-085 justify-in-start mb-24">
                                    Have a large team? Select our enterprise solution
                                </div>
                                <div className="pricing-plan-card justify-space-between" style={{ gap: 24 }}>
                                    <div>
                                        <div className="manrope f-20 w-700 lh-32 black-085">
                                            {businessPlan.name}
                                        </div>
                                        <div className="manrope f-16 w-500 lh-24 black-085 mb-8">
                                            {businessPlan.features.join(' \u2022 ')}
                                        </div>
                                    </div>
                                    <div className="pricing-plans-actions">
                                        <Button
                                            onClick={() => window.location.href = `${PORTAL_LINK}contact-us`}
                                            className="schedule-button mb-8">
                                            {businessPlan.buttonText}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>}
                </div>
            </div>
        </Modal>
    );
};

export default PricingPlans;
